// TMOS Plan Service
// Date: January 13, 2026
// Purpose: Frontend service for plan management

import api from './api';
import { Plan, CreatePlanRequest, UpdatePlanRequest } from '../types/plan';
import { PlanHealth } from '../types/businessPerformance';

export interface PlanFilters {
  plan_type?: 'STRATEGIC' | 'ANNUAL' | 'QUARTERLY';
  plan_year?: number;
  status?: 'DRAFT' | 'PROPOSED' | 'APPROVED' | 'ACTIVE' | 'DELETED';
  business_hierarchy_id?: string;
  functional_hierarchy_id?: string;
  geographic_hierarchy_id?: string;
}

export const planService = {
  /**
   * Get all plans for tenant
   */
  async getAllPlans(filters?: PlanFilters): Promise<Plan[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<{ success: boolean; data: Plan[]; total: number }>(
      `/plans${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.data;
  },

  /**
   * Get plan by ID
   */
  async getPlanById(planId: string): Promise<Plan> {
    const response = await api.get<{ success: boolean; data: Plan }>(`/plans/${planId}`);
    return response.data.data;
  },

  /**
   * Create new plan
   */
  async createPlan(planData: CreatePlanRequest): Promise<Plan> {
    const response = await api.post<{ success: boolean; message: string; data: Plan }>(
      '/plans',
      planData
    );
    return response.data.data;
  },

  /**
   * Update plan
   */
  async updatePlan(planId: string, planData: UpdatePlanRequest): Promise<Plan> {
    const response = await api.put<{ success: boolean; message: string; data: Plan }>(
      `/plans/${planId}`,
      planData
    );
    return response.data.data;
  },

  /**
   * Delete plan (soft delete)
   */
  async deletePlan(planId: string): Promise<void> {
    await api.delete<{ success: boolean; message: string }>(`/plans/${planId}`);
  },

  /**
   * Get plan health
   */
  async getPlanHealth(planId: string): Promise<PlanHealth> {
    const response = await api.get<{ success: boolean; data: PlanHealth }>(
      `/plans/${planId}/health`
    );
    return response.data.data;
  }
};

